class UnityGameLoader {
    constructor(config = {}) {
        this.container = document.querySelector("#unity-container");
        this.canvas = document.querySelector("#unity-canvas");
        this.loadingBar = document.querySelector("#unity-loading-bar");
        this.progressBarFull = document.querySelector("#unity-progress-bar-full");
        this.fullscreenButton = document.querySelector("#unity-fullscreen-button");
        this.warningBanner = document.querySelector("#unity-warning");
        
        this.config = {
            buildUrl: config.buildUrl || "Build",
            apiEndpoint: config.apiEndpoint || "/api/unity-files",
            companyName: config.companyName || "DefaultCompany",
            productName: config.productName || "My project",
            productVersion: config.productVersion || "0.1",
            streamingAssetsUrl: config.streamingAssetsUrl || "StreamingAssets",
            matchWebGLToCanvasSize: config.matchWebGLToCanvasSize !== false,
            devicePixelRatio: config.devicePixelRatio || window.devicePixelRatio
        };

        this.unityInstance = null;
    }

    async fetchFile(filename) {
        try {
            const response = await fetch(`${this.config.apiEndpoint}/${filename}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch ${filename}: ${response.statusText}`);
            }
            return await response.blob();
        } catch (error) {
            console.error(`Error fetching ${filename}:`, error);
            throw error;
        }
    }

    createObjectURL(blob) {
        return URL.createObjectURL(blob);
    }

    showBanner(msg, type) {
        const updateBannerVisibility = () => {
            this.warningBanner.style.display = 
                this.warningBanner.children.length ? 'block' : 'none';
        };

        const div = document.createElement('div');
        div.innerHTML = msg;
        this.warningBanner.appendChild(div);

        if (type === 'error') {
            div.style = 'background: red; padding: 10px;';
        } else if (type === 'warning') {
            div.style = 'background: yellow; padding: 10px;';
            setTimeout(() => {
                this.warningBanner.removeChild(div);
                updateBannerVisibility();
            }, 5000);
        }

        updateBannerVisibility();
    }

    setupMobileDevice() {
        const meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content = 'width=device-width, height=device-height, initial-scale=1.0, user-scalable=no, shrink-to-fit=yes';
        document.getElementsByTagName('head')[0].appendChild(meta);
        
        this.container.className = "unity-mobile";
        this.canvas.className = "unity-mobile";
    }

    setupDesktop() {
        this.canvas.style.width = "960px";
        this.canvas.style.height = "600px";
    }

    async loadGame() {
        try {
            // Show loading bar
            this.loadingBar.style.display = "block";

            // Fetch all required files
            const [loaderBlob, dataBlob, frameworkBlob, wasmBlob] = await Promise.all([
                this.fetchFile('release.loader.js'),
                this.fetchFile('release.data.gz'),
                this.fetchFile('release.framework.js.gz'),
                this.fetchFile('release.wasm.gz')
            ]);

            // Create object URLs
            const loaderUrl = this.createObjectURL(loaderBlob);
            const dataUrl = this.createObjectURL(dataBlob);
            const frameworkUrl = this.createObjectURL(frameworkBlob);
            const codeUrl = this.createObjectURL(wasmBlob);

            // Setup configuration
            const unityConfig = {
                dataUrl,
                frameworkUrl,
                codeUrl,
                streamingAssetsUrl: this.config.streamingAssetsUrl,
                companyName: this.config.companyName,
                productName: this.config.productName,
                productVersion: this.config.productVersion,
                showBanner: (msg, type) => this.showBanner(msg, type),
                matchWebGLToCanvasSize: this.config.matchWebGLToCanvasSize
            };

            // Check for mobile device
            if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                this.setupMobileDevice();
                unityConfig.devicePixelRatio = this.config.devicePixelRatio;
            } else {
                this.setupDesktop();
            }

            // Load the Unity loader script
            const script = document.createElement("script");
            script.src = loaderUrl;
            
            // Create and initialize Unity instance
            script.onload = () => {
                createUnityInstance(this.canvas, unityConfig, (progress) => {
                    this.progressBarFull.style.width = `${100 * progress}%`;
                }).then((instance) => {
                    this.unityInstance = instance;
                    this.loadingBar.style.display = "none";
                    
                    this.fullscreenButton.onclick = () => {
                        this.unityInstance.SetFullscreen(1);
                    };

                    // Cleanup object URLs
                    URL.revokeObjectURL(loaderUrl);
                    URL.revokeObjectURL(dataUrl);
                    URL.revokeObjectURL(frameworkUrl);
                    URL.revokeObjectURL(codeUrl);
                }).catch((message) => {
                    this.showBanner(message, 'error');
                });
            };

            document.body.appendChild(script);
        } catch (error) {
            this.showBanner(`Failed to load game: ${error.message}`, 'error');
            throw error;
        }
    }
}

export default UnityGameLoader;

// Usage example:
// const gameLoader = new UnityGameLoader({
//     apiEndpoint: '/api/unity-files',
//     companyName: 'MyCompany',
//     productName: 'MyGame',
//     productVersion: '1.0'
// });
// gameLoader.loadGame().catch(console.error);